/* ==========================================================================
   UiPopper > Client > Prototype
   ========================================================================== */

/* Modules */
const { Store } = require('@bluekiri/alexandria/helpers/state');
const { fromEvent, merge } = require('rxjs');
const { filter, tap } = require('rxjs/operators');

const { actions, types } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { addPopper, destroyPopper } = require('./popper');

const ESCAPE_CODE = 27;

/* Prototype */
const prototype = {
    /* Setup store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            uipopper: reducer
        });

        return this;
    },
    /* Setup actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            destroy: () => {
                return this.store.dispatch(actions.destroy());
            },
            display: () => {
                return this.store.dispatch(actions.display());
            },
            hide: () => {
                return this.store.dispatch(actions.hide());
            }
        };

        return this;
    },

    /* Setup reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Remove element */
                this.element.parentNode.removeChild(this.element);

                /* Clean */
                [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());
                [...Object.keys(this.reactions)].forEach(key => this.reactions[key].unsubscribe());
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),

            display: this.store.subscribe((state, action) => {
                if (action.type !== types.DISPLAY) return;

                addPopper(this);
            }),
            hide: this.store.subscribe((state, action) => {
                if (action.type !== types.HIDE) return;

                destroyPopper(this);
            })
        };

        return this;
    },

    /* Setup events */
    setupEvents() {
        if (!this.config.events) return this;

        /* Window clicks */
        const windowClick$ = fromEvent(window.document, 'click').pipe(
            filter(({ target }) => !target.closest('[data-component="UiPopper"]'))
        );

        /* click on tag trigger */
        const clickSelf$ = fromEvent(this.element, 'click');
        // const transitionEnd$ = fromEvent(this.element, 'transitionend');

        /* Clicks merged */
        const mergedClick$ = merge(clickSelf$, windowClick$);

        /* Press escape key */
        const keyup$ = fromEvent(window.document, 'keyup').pipe(filter(event => event.keyCode === ESCAPE_CODE));

        /* Events */
        this.events = {
            click$: mergedClick$.pipe(tap(event => event.stopPropagation())),
            keyup$
        };

        return this;
    },

    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        this.subscriptions = {};

        // enter and leve mouse events
        if (!this.config.hasClick && this.config.events)
            this.subscriptions = {
                mouseenter: fromEvent(this.element, 'mouseenter').subscribe(event => {
                    event.stopPropagation();

                    /* Data */
                    const { hidden } = this.store.select('uipopper');
                    if (!this.config.hasClick && hidden) this.actions.display();
                }),
                mouseleave: fromEvent(this.element, 'mouseleave').subscribe(event => {
                    event.stopPropagation();

                    /* Data */
                    const { hidden } = this.store.select('uipopper');

                    if (!this.config.hasClick && !hidden) {
                        this.actions.hide();
                    }
                })
            };

        // click and keyboard events
        if (this.config.hasClick && this.config.events)
            this.subscriptions = {
                ...this.subscriptions,
                click: this.events.click$.subscribe(event => {
                    event.stopPropagation();
                    /* Data */
                    const { hidden } = this.store.select('uipopper');

                    /* show/hide popper */
                    if (!event.target.matches('[data-uipopper-content]') && !hidden) this.actions.hide();
                    else if (event.target.matches('[data-component="UiPopper"]') && hidden) {
                        this.actions.display();
                    }
                }),
                keyup: this.events.keyup$.subscribe(() => {
                    const { hidden } = this.store.select('uipopper');

                    if (!hidden) this.actions.hide();
                })
            };

        return this;
    },

    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },

    /* Init */
    init() {
        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Setup events */
        this.setupEvents();

        /* Subscribe */
        this.subscribe();
    }
};

/* Exports */
module.exports = prototype;
